import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import moment from 'moment'

import CrashLogo from 'modules/core/components/CrashLogo'
import hasFeature from 'modules/core/lib/hasFeature'

import type Employee from 'modules/employees/store/models/Employee'
import { isDailyContract } from 'modules/timeoff/lib/contractsVersion'
import { useContractsVersion } from 'modules/timeoff/lib/contractsVersionContext'
import { type AllowanceIncidences } from 'modules/timeoff/store/collections/allowanceIncidences'
import { type Allowances } from 'modules/timeoff/store/collections/allowances'
import { type TimeoffPolicies } from 'modules/timeoff/store/collections/timeoffPolicies/policies'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import type { PolicyTimelineItem } from 'modules/timeoff/store/models/TimeoffPolicy/PolicyTimeline'

import LegacyStatsCounter from './LegacyStatsCounter'
import List from './List'
import NewStatsCounter from './NewStatsCounter'
import ToggleList from './ToggleList'

interface Props {
  employee: Employee
  policyTimelineItem: PolicyTimelineItem
  allowances: Allowances
  allowanceIncidences: AllowanceIncidences
  policies: TimeoffPolicies
}

const Overview = ({
  employee,
  policyTimelineItem,
  allowances,
  allowanceIncidences,
  policies,
}: Props) => {
  const [isExpanded, toggleExpand] = React.useState(false)
  const { contract } = useContractsVersion()

  const allowancePolicy = (allowance: Allowance) => {
    const timeoffPolicyId = allowance.attributes.timeoff_policy_id
    return policies.get(timeoffPolicyId)
  }

  const policyAllowances = allowances.sortedByPosition.filter((allowance: Allowance) => {
    return allowancePolicy(allowance)?.id == policyTimelineItem?.timeoff_policy_id
  })

  if (policyAllowances.length === 0) {
    return <CrashLogo />
  }

  const filteredEmployeeAllowancesByType = isDailyContract(contract)
    ? policyAllowances.filter((allowance: Allowance) => !allowance.isOvertime)
    : policyAllowances

  const filteredEmployeeAllowances = filteredEmployeeAllowancesByType.filter(
    (allowance: Allowance) =>
      !allowance.isTimeWorked || (allowance.isTimeWorked && !!employee.isIncludeInTimeTracking)
  )

  const firstTwoAllowances = filteredEmployeeAllowances.slice(0, 2)
  const otherAllowances = filteredEmployeeAllowances.slice(2)
  const isShowListVisible = !!otherAllowances.length

  const isCurrent = policyTimelineItem?.isCurrent
  const isPast = policyTimelineItem?.isPast
  const referenceDate = isCurrent
    ? moment()
    : isPast
    ? policyTimelineItem?.endAt
    : policyTimelineItem?.startAt

  return (
    <>
      <Box gap="s32">
        {firstTwoAllowances.map((allowance: Allowance) => {
          if (hasFeature('dev_new_timeoff_counters')) {
            return <NewStatsCounter key={allowance.id} employee={employee} allowance={allowance} />
          }

          return (
            <LegacyStatsCounter
              key={`${allowance.id}-${policyTimelineItem.policy_assignment_id}`}
              employee={employee}
              allowance={allowance}
              allowanceIncidences={allowanceIncidences}
              originalReferenceDate={referenceDate}
            />
          )
        })}
      </Box>
      {(isShowListVisible || isExpanded) && (
        <Box gap="s16">
          {isShowListVisible && (
            <ToggleList
              statsLength={otherAllowances.length}
              toogleExpand={toggleExpand}
              isExpanded={isExpanded}
            />
          )}
          {isExpanded && (
            <List
              employee={employee}
              allowances={otherAllowances}
              allowanceIncidences={allowanceIncidences}
              referenceDate={referenceDate}
            />
          )}
        </Box>
      )}
    </>
  )
}

export default Overview
